import React, { useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Input,
  List,
  Modal,
  Pagination,
  Space,
  Tag,
  Typography,
} from "antd";
import type { InputRef } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { COLORS } from "../../utils/constants";
import {
  OpportunityListItem,
  OpportunityRecord,
} from "../../pages/tender/helper/opportunities";

const { Text, Title } = Typography;

const normalizeStatus = (status: string) => {
  // Strip XML hex-encoded spaces (_x0020_) that BC sometimes returns, e.g.
  // "In_x0020_Progress" -> "In Progress".
  const statusLower = (status || "").replace(/_x0020_/gi, " ").toLowerCase().trim();
  return statusLower === "draft" || statusLower === "in progress" ? "in-progress" : statusLower;
};

const toPascalCase = (str: string) =>
  str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const getStatusColor = (status: string) => {
  const statusNormalized = normalizeStatus(status);
  switch (statusNormalized) {
    case "in-progress":
      return "blue";
    case "under review - senior estimator":
      return "orange";
    case "under review commercial director":
      return "gold";
    case "under client review":
      return "green";
    case "approved":
    case "won":
      return "success";
    case "submitted":
      return "processing";
    case "pending":
      return "warning";
    case "lost":
      return "error";
    case "closed":
      return "default";
    default:
      return "default";
  }
};

interface OpportunityImportCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  accentColor: string;
  backgroundColor: string;
  opportunities: OpportunityRecord[];
  opportunitiesLoading?: boolean;
  userRegion?: string | null;
  imported: OpportunityRecord | null;
  isImporting?: boolean;
  onImport: (record: OpportunityRecord) => void;
  onChangeOpportunity?: () => void;
  onClear?: () => void;
  isActiveSource?: boolean;
  onSelectAsSource?: () => void;
  manualPreview?: {
    projectName: string;
    client: string;
    siteAddress?: string;
    region: string;
  };
}

const OpportunityImportCard: React.FC<OpportunityImportCardProps> = ({
  title,
  description,
  icon,
  accentColor,
  backgroundColor,
  opportunities,
  opportunitiesLoading = false,
  userRegion,
  imported,
  isImporting = false,
  onImport,
  onChangeOpportunity,
  onClear,
  isActiveSource = false,
  onSelectAsSource,
  manualPreview,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const searchInputRef = useRef<InputRef>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const filteredOpportunities = useMemo(() => {
    if (!modalOpen) return [];

    // Same filtering as /tender-info's "Import Existing Opportunity" modal:
    // only In Progress, non-closed opportunities, scoped to the user's
    // profile region (skipped if "All Regions").
    let filtered = opportunities.filter((opp) => {
      const normalizedStatus = (opp.status || "")
        .replace(/_x0020_/gi, " ")
        .toLowerCase()
        .trim();
      return normalizedStatus === "in progress" && (opp as { closed?: boolean }).closed !== true;
    });

    if (userRegion && userRegion.trim().toLowerCase() !== "all regions") {
      filtered = filtered.filter(
        (opp) => opp.region?.trim().toLowerCase() === userRegion.trim().toLowerCase(),
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (opp) =>
          opp.no?.toLowerCase().includes(query) ||
          opp.tender?.toLowerCase().includes(query) ||
          opp.client?.toLowerCase().includes(query) ||
          opp.siteAddress?.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [opportunities, searchQuery, modalOpen, userRegion]);

  const pagedOpportunities = useMemo(
    () => filteredOpportunities.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredOpportunities, currentPage],
  );

  const handleOpen = () => {
    setSelectedId(imported?.tenderId || null);
    setCurrentPage(1);
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedId(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleConfirmImport = () => {
    const record = opportunities.find((opp) => opp.tenderId === selectedId);
    if (record) {
      onImport(record);
      setModalOpen(false);
      setSelectedId(null);
      setSearchQuery("");
    }
  };

  const hasManualDetails = !!manualPreview?.projectName?.trim();

  return (
    <Card
      onClick={onSelectAsSource}
      style={{
        backgroundColor,
        border: isActiveSource
          ? `2px solid ${accentColor}`
          : imported
            ? "2px solid #d9d9d9"
            : `1px solid ${backgroundColor}`,
        borderRadius: "8px",
        height: "100%",
        position: "relative",
        cursor: onSelectAsSource ? "pointer" : "default",
      }}
      bodyStyle={{ padding: "24px" }}
    >
      {(imported || (manualPreview && hasManualDetails)) && onClear && (
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label="Clear opportunity details"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          style={{ position: "absolute", top: 8, right: 8, color: "#6b7280" }}
        />
      )}
      <div className="flex items-start gap-4 mb-4">
        <div
          style={{
            width: "48px",
            height: "48px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div>
          <Title
            level={5}
            style={{ margin: 0, marginBottom: "4px", fontSize: "16px", color: "#111827" }}
          >
            {title}
          </Title>
          <Text style={{ color: "#6b7280", fontSize: "13px" }}>
            {description}
          </Text>
        </div>
      </div>

      {imported ? (
        <div>
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "6px",
              padding: "12px 14px",
              marginBottom: "12px",
            }}
          >
            <Text strong className="block text-gray-900 mb-1">
              {imported.tender || "Unnamed Project"}
            </Text>
            {imported.no && (
              <Text className="text-gray-500 block text-xs mb-1">
                Opportunity No: {imported.no}
              </Text>
            )}
            <Text className="text-gray-500 block text-xs mb-1">
              Client: {imported.client || "Not specified"}
            </Text>
            {imported.siteAddress && (
              <Text className="text-gray-500 block text-xs mb-1">
                Site: {imported.siteAddress}
              </Text>
            )}
            <Space size={4}>
              <Tag color={getStatusColor(imported.status)}>
                {toPascalCase(normalizeStatus(imported.status))}
              </Tag>
              {isActiveSource && onSelectAsSource && (
                <Tag color={accentColor}>In use on form</Tag>
              )}
            </Space>
          </div>
          {onSelectAsSource && !isActiveSource && (
            <Text className="block text-xs mb-2" style={{ color: accentColor }}>
              Click card to use these details on the form
            </Text>
          )}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onChangeOpportunity?.();
              handleOpen();
            }}
            loading={isImporting}
          >
            Change Opportunity
          </Button>
        </div>
      ) : (
        <div>
          {manualPreview && hasManualDetails ? (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "6px",
                padding: "12px 14px",
                marginBottom: "12px",
              }}
            >
              <Text strong className="block text-gray-900 mb-1">
                {manualPreview.projectName}
              </Text>
              <Text className="text-gray-500 block text-xs mb-1">
                Client: {manualPreview.client || "Not specified"}
              </Text>
              {manualPreview.siteAddress && (
                <Text className="text-gray-500 block text-xs mb-1">
                  Site: {manualPreview.siteAddress}
                </Text>
              )}
              {manualPreview.region && (
                <Text className="text-gray-500 block text-xs mb-1">
                  Region: {manualPreview.region}
                </Text>
              )}
              <Space size={4}>
                <Tag color="default">Manual Entry</Tag>
                {isActiveSource && onSelectAsSource && (
                  <Tag color={accentColor}>In use on form</Tag>
                )}
              </Space>
            </div>
          ) : (
            onSelectAsSource && (
              <div className="mb-3">
                {isActiveSource ? (
                  <Text className="block text-xs" style={{ color: accentColor }}>
                    No details entered yet — fill in the form below.
                  </Text>
                ) : (
                  <Text className="block text-xs" style={{ color: accentColor }}>
                    Click card to enter these details manually on the form
                  </Text>
                )}
              </div>
            )
          )}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleOpen();
            }}
            loading={opportunitiesLoading || isImporting}
            style={{
              backgroundColor: COLORS.primaryLight,
              borderColor: COLORS.primaryLight,
              color: COLORS.primary,
              fontWeight: 500,
            }}
          >
            Import Existing Opportunity
          </Button>
        </div>
      )}

      <Modal
        open={modalOpen}
        onCancel={handleCancel}
        width={800}
        destroyOnClose
        // Focus the search input once the modal has finished its open
        // transition — doing it here (rather than the Input's own
        // `autoFocus`) avoids antd's Modal focus-trap grabbing focus back to
        // the dialog wrapper right after autoFocus fires on mount.
        afterOpenChange={(open) => {
          if (open) {
            searchInputRef.current?.focus();
          }
        }}
        title={
          <Typography.Title level={4} className="text-blue-900 mb-0">
            Import {title}
          </Typography.Title>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={handleCancel} className="text-gray-600 hover:text-gray-800">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              type="primary"
              disabled={!selectedId}
              loading={isImporting}
              className="bg-blue-900 hover:bg-blue-800 border-blue-900"
            >
              Import Client Details
            </Button>
          </div>
        }
      >
        <div className="py-4">
          {opportunities.length > 0 && (
            <>
              <Text className="mb-3 text-gray-600 block">
                Select an opportunity to load and continue working on it:
              </Text>
              <Input
                ref={searchInputRef}
                placeholder="Search by opportunity no., project name, client, or site address..."
                prefix={<SearchOutlined className="text-gray-400" />}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                allowClear
                className="mb-4"
                size="large"
              />
            </>
          )}

          <div className="border border-gray-200 rounded-lg">
            <div className="max-h-96 overflow-auto">
              <List
                dataSource={pagedOpportunities}
                renderItem={(project: OpportunityRecord) => (
                  <OpportunityListItem
                    key={project.tenderId}
                    project={project}
                    isSelected={selectedId === project.tenderId}
                    onSelect={setSelectedId}
                  />
                )}
                locale={{
                  emptyText: searchQuery
                    ? `No opportunities found matching "${searchQuery}"`
                    : "No opportunities available",
                }}
              />
            </div>
            {filteredOpportunities.length > PAGE_SIZE && (
              <div className="flex justify-end px-4 py-2 border-t border-gray-200">
                <Pagination
                  current={currentPage}
                  pageSize={PAGE_SIZE}
                  total={filteredOpportunities.length}
                  showSizeChanger={false}
                  showTotal={(total) => `Total ${total} opportunities`}
                  size="small"
                  onChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default OpportunityImportCard;
